import { useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  Divider,
  Text,
  Loader,
  Button,
  Tooltip,
  Stack,
  Card,
  Badge,
  Group,
  Code,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { getConfigurationTemplateSafe } from '@services/configurationTemplateManager';
import {
  Clock,
  PlayCircle,
  StopCircle,
  XCircle,
  Hourglass,
  Trash2,
} from 'lucide-react';
import { appConfigs } from '@configs/apps/appConfigs';
import { useSession } from '../../hooks/useSession';
import { useUserDevices } from '../../hooks/useUserDevices';
import { useBaselineCalculationStore } from '../../store/useBaselineCalculationStore';
import { useHeartRateStore } from '../../store/useHeartRateStore';
import { useDeviceConnectionsStatus } from '../../hooks/useDeviceConnectionsStatus';
import { BaselineCalculator, SessionLoadingCard } from './_components';
import { SessionTimer } from './_components/SessionTimer/SessionTimer';
import { useAuthUser } from '@store/useAuthStore';
import { useBaselineCalculation } from '../../hooks/useBaselineCalculation';
import { getDeviceStatus } from '@utils';
import { formatFromSecondToMinutes } from '@utils/formatDuration';
import { requiresBaseline } from '@utils/sessionHelpers';
import { useDebugEnabled } from '../../utils/debugFlag';
import { SessionConfigSummary } from '../../features/dev-tools';
import {
  buildLegacyInitSessionConfig,
  normalizeInitSessionConfig,
} from '../../utils/extractSessionObject';
import { getVideoById } from '@services/mediaManager';
import type { VideoConfigStep } from '@types';
import StatPhase from './_components/StatPhase';
import PageHeader from '../../components/shared/PageHeader';
import { getScenarioLayout } from './_components/scenarios/ScenarioLayoutFactory';
import type { InitSessionButtonProps } from '@types';
import type { DeviceConnectionStatus, Device } from '@types';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';

/** Utente per cui "Inizia sessione" resta sempre cliccabile (es. test senza VR). */
const SESSION_START_UNGATED_USER_ID = 153;

// ============================================================================
// Presentational Components
// ============================================================================

interface SessionHeaderProps {
  sessionId?: number;
  deleteSession?: (sessionId: number | string) => void;
  isDeleting?: boolean;
  sessionStatus?: string;
}

const SessionHeader = React.memo<SessionHeaderProps>(
  ({ sessionId, deleteSession, isDeleting, sessionStatus }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data: template } = useQuery({
      queryKey: ['session-configuration', sessionId],
      queryFn: async () => {
        if (!sessionId) return null;
        return await getConfigurationTemplateSafe(sessionId);
      },
      enabled: Boolean(sessionId),
    });

    const handleDelete = () => {
      if (!sessionId || !deleteSession) return;

      modals.openConfirmModal({
        title: t('confirm_delete_session', 'Conferma eliminazione sessione'),
        children: (
          <Text size='sm'>
            {t(
              'confirm_delete_session_text',
              'Sei sicuro di voler eliminare questa sessione? Questa azione non può essere annullata.'
            )}
          </Text>
        ),
        labels: {
          confirm: t('confirm', 'Conferma'),
          cancel: t('cancel', 'Annulla'),
        },
        confirmProps: { color: 'red' },
        onCancel: () => {},
        onConfirm: () => deleteSession(sessionId),
      });
    };

    const pageTitle =
      template?.title || template?.name || `Sessione numero ${sessionId || ''}`;
    const pageSubtitle = template?.description;

    return (
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        breadcrumbs={[
          {
            label: t('all_sessions', 'Tutte le sessioni'),
            onClick: () => navigate('/sessions'),
          },
          {
            label: `Sessione numero ${sessionId || ''}`,
          },
        ]}
        backUrl='/sessions'
        actions={
          <Button
            variant='outline'
            size='sm'
            color='red'
            onClick={handleDelete}
            loading={isDeleting}
            disabled={!deleteSession || sessionStatus === 'in-progress'}
          >
            <Trash2 style={{ marginRight: 6 }} />
            {t('delete_session', 'Elimina sessione')}
          </Button>
        }
      />
    );
  }
);

SessionHeader.displayName = 'SessionHeader';

const InitSessionButton = React.memo<InitSessionButtonProps>(
  ({ appRequirements, isVrConnected, initSession, t }) => {
    const user = useAuthUser();
    const ignoreVrForStart = user?.id === SESSION_START_UNGATED_USER_ID;

    const isVrRequiredButDisconnected =
      !ignoreVrForStart && appRequirements.vr && !isVrConnected;
    const tooltipLabel = isVrRequiredButDisconnected
      ? 'Dispositivo richiesto: visore VR. Deve essere connesso per iniziare la sessione.'
      : '';
    const tooltipDisabled = !isVrRequiredButDisconnected;

    const buttonDisabled = isVrRequiredButDisconnected;

    return (
      <Tooltip
        label={tooltipLabel}
        position='top'
        withArrow
        disabled={tooltipDisabled}
      >
        <Button
          color='blue'
          fullWidth
          disabled={buttonDisabled}
          radius='sm'
          onClick={initSession}
          size='md'
        >
          {t('start_session', 'Inizia sessione')}
        </Button>
      </Tooltip>
    );
  }
);

InitSessionButton.displayName = 'InitSessionButton';

interface SessionControlsProps {
  session: DeviceConfigurationApi | undefined;
  appRequirements: { vr: boolean; sensor: boolean };
  allDevices: Device[];
  connectionStatus: Record<string, DeviceConnectionStatus>;
}

// ============================================================================
// Custom Hook for Session Controls Base Logic
// ============================================================================

const useSessionControlsBase = (
  session: DeviceConfigurationApi | undefined,
  allDevices: Device[],
  connectionStatus: Record<string, DeviceConnectionStatus>
) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const {
    initSession,
    startBaseline,
    startExperience,
    forceStartExperience,
    skipBaseline,
    terminateSession,
    cancelSession,
    isPending,
  } = useSession(id);

  const { baseline } = useBaselineCalculation();
  const sessionState = session?.status || 'queued';

  const deviceState = useMemo(() => {
    const result = allDevices.map(device => {
      const clientId = device.clientId ?? device.code;
      const status =
        connectionStatus[clientId ?? ''] ?? device.status ?? 'disconnected';

      return {
        ...device,
        status,
      };
    });

    return result;
  }, [allDevices, connectionStatus]);

  const isSensorConnected =
    getDeviceStatus(deviceState, 'smartwatch') === 'connected';
  const vrStatus = getDeviceStatus(deviceState, 'vr-headset');
  const isVrConnected =
    vrStatus === 'connected' || vrStatus === 'receiving_data';
  const deviceStatus =
    deviceState.find(d => d.type === 'vr-headset')?.status || 'disconnected';
  const sensorStatus =
    deviceState.find(d => d.type === 'smartwatch')?.status || 'disconnected';

  return {
    sessionState,
    baseline,
    isSensorConnected,
    isVrConnected,
    deviceStatus,
    sensorStatus,
    initSession,
    startBaseline,
    startExperience,
    forceStartExperience,
    skipBaseline,
    terminateSession,
    cancelSession,
    isPending,
    t,
  };
};

// ============================================================================
// Base Session Controls Content Component
// ============================================================================

// ============================================================================
// Helper to get status badge color and icon
// ============================================================================

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'in-progress':
      return {
        color: 'green' as const,
        icon: PlayCircle,
        label: 'In corso',
        textColor: 'var(--mantine-color-green-6)',
      };
    case 'initializing':
      return {
        color: 'blue' as const,
        icon: Loader,
        label: 'Inizializzazione',
        textColor: 'var(--mantine-color-blue-6)',
      };
    case 'loading':
      return {
        color: 'blue' as const,
        icon: Loader,
        label: 'Avvio in corso',
        textColor: 'var(--mantine-color-blue-6)',
      };
    case 'completed':
      return {
        color: 'gray' as const,
        icon: Clock,
        label: 'Completata',
        textColor: 'var(--mantine-color-gray-6)',
      };
    case 'queued':
    default:
      return {
        color: 'yellow' as const,
        icon: Hourglass,
        label: 'In attesa',
        textColor: 'var(--mantine-color-yellow-6)',
      };
  }
};

// ============================================================================
// Session Status Card Component
// ============================================================================

interface SessionStatusCardProps {
  sessionState: string;
  session?: DeviceConfigurationApi | undefined;
  baseline: number;
  isSensorConnected: boolean;
  isVrConnected: boolean;
  sensorStatus: string;
  deviceStatus: string;
  appRequirements: { vr: boolean; sensor: boolean };
  isPending: boolean;
  onInitSession?: () => void;
  onTerminateSession?: () => Promise<void>;
  onCancelSession?: () => Promise<void>;
  onForceStartExperience?: () => void;
  showForceStartExperience?: boolean;
  onStartBaseline?: () => void;
  onSkipBaseline?: () => void;
  initSessionPreview?: {
    eventType: 'initSession' | 'startSession';
    payload: Record<string, unknown>;
  };
  t: ReturnType<typeof useTranslation>['t'];
}

const SessionStatusCard = ({
  sessionState,
  session,
  baseline: _baseline, // eslint-disable-line @typescript-eslint/no-unused-vars
  isSensorConnected,
  isVrConnected,
  sensorStatus,
  deviceStatus,
  appRequirements,
  isPending,
  onInitSession,
  onTerminateSession,
  onCancelSession,
  onForceStartExperience,
  showForceStartExperience,
  onStartBaseline,
  onSkipBaseline,
  initSessionPreview,
  t,
}: SessionStatusCardProps) => {
  const isDebugMode = useDebugEnabled();
  // Quando isPending è true e siamo in queued, mostriamo "loading" come stato
  const displayState =
    isPending && sessionState === 'queued' ? 'loading' : sessionState;
  const statusConfig = getStatusConfig(displayState);
  const StatusIcon = statusConfig.icon;
  const isWaitingForVrConnection =
    sessionState === 'queued' && appRequirements.vr && !isVrConnected;
  const queuedStatusLabel = isWaitingForVrConnection ? 'In attesa' : 'Pronta';

  // Per exposure non normalizzare a steps: la config è { itemIds, multiItem, hasTutorial, ... }
  const normalizedConfig = useMemo(() => {
    if (!session?.config) return [];
    if (session.type === 'exposure') return [];
    return normalizeInitSessionConfig(session.config);
  }, [session?.config, session?.type]);

  // Controlla se la config ha question (solo per tipi con steps, non exposure)
  const hasQuestion = useMemo(() => {
    return normalizedConfig.some(
      step => 'question' in step && step.question !== undefined
    );
  }, [normalizedConfig]);

  // Trova il primo step VIDEO nella config
  const firstVideoStep = useMemo(() => {
    if (!hasQuestion) return null;
    return normalizedConfig.find(
      step => 'video' in step && step.video && step.video !== ''
    ) as VideoConfigStep | undefined;
  }, [hasQuestion, normalizedConfig]);

  // Ottieni il video dal videoId se presente
  const { data: firstVideo } = useQuery({
    queryKey: ['video', firstVideoStep?.video],
    queryFn: () => {
      if (!firstVideoStep?.video) return null;
      return getVideoById(firstVideoStep.video);
    },
    enabled: Boolean(hasQuestion && firstVideoStep?.video),
  });

  const experienceDuration = useMemo(() => {
    if (hasQuestion && firstVideo?.duration) {
      return firstVideo.duration;
    }
    return session?.config?.maxDuration || session?.video?.duration || 0;
  }, [
    hasQuestion,
    firstVideo?.duration,
    session?.config?.maxDuration,
    session?.video?.duration,
  ]);

  // Calcola se il BaselineCalculator è disabilitato
  // Se siamo in inizializzazione e i dispositivi sono disconnessi, mostra solo "Salta baseline"
  const isBaselineDisabled = useMemo(() => {
    if (sessionState !== 'initializing') return false;
    // Se almeno uno dei dispositivi è disconnesso, mostra solo "Salta baseline"
    return !isSensorConnected || !isVrConnected;
  }, [sessionState, isSensorConnected, isVrConnected]);

  return (
    <Card shadow='sm' withBorder p='md' radius='sm'>
      <Stack gap='md'>
        {initSessionPreview && (
          <Accordion variant='contained'>
            <Accordion.Item value='init-session-preview'>
              <Accordion.Control>
                <Text size='xs' fw={600} c='dimmed'>
                  Payload &quot;Inizia sessione&quot; (preview)
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Code block>{JSON.stringify(initSessionPreview, null, 2)}</Code>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
        {isDebugMode && session ? (
          <SessionConfigSummary session={session} />
        ) : null}
        <Group gap='sm' align='center'>
          <StatusIcon
            size={20}
            style={{ color: statusConfig.textColor }}
            className={
              displayState === 'initializing' || displayState === 'loading'
                ? 'animate-spin'
                : ''
            }
          />
          <Text fw={500} size='md'>
            Stato
          </Text>
          <Badge
            color={statusConfig.color}
            variant='light'
            size='lg'
            radius='md'
          >
            {sessionState === 'queued' ? queuedStatusLabel : statusConfig.label}
          </Badge>
        </Group>

        {(sessionState === 'queued' ||
          (isPending && sessionState === 'queued')) && (
          <>
            {experienceDuration > 0 && (
              <Group gap='xs' align='center'>
                <Clock
                  size={16}
                  style={{ color: 'var(--mantine-color-dimmed)' }}
                />
                <Text size='sm' c='dimmed'>
                  Durata:
                </Text>
                <Text size='sm' fw={600}>
                  {formatFromSecondToMinutes(experienceDuration)}
                </Text>
              </Group>
            )}

            <Text size='sm' c='dimmed'>
              {isPending
                ? 'Avvio della sessione in corso...'
                : "La sessione è pronta per l'avvio."}
            </Text>

            {onInitSession && (
              <InitSessionButton
                appRequirements={appRequirements}
                isVrConnected={isVrConnected}
                isSensorConnected={isSensorConnected}
                deviceState={sensorStatus}
                sessionState={isPending ? 'loading' : sessionState}
                initSession={onInitSession}
                t={t}
              />
            )}
          </>
        )}

        {sessionState === 'initializing' &&
          onStartBaseline &&
          onSkipBaseline && (
            <BaselineCalculator
              isDisabled={isBaselineDisabled}
              deviceStatus={deviceStatus as DeviceConnectionStatus}
              sensorStatus={sensorStatus as DeviceConnectionStatus}
              onStart={onStartBaseline}
              onSkip={onSkipBaseline}
              label={'Calcola baseline'}
            />
          )}

        {sessionState === 'in-progress' && (
          <>
            <SessionTimer session={session} />

            <Text size='sm' c='dimmed'>
              La sessione è attualmente in esecuzione. Puoi terminarla o
              annullarla.
            </Text>

            {onTerminateSession && onCancelSession && (
              <Stack gap='sm' mt='xs'>
                <Group gap='sm'>
                  <Button
                    leftSection={<StopCircle size={18} />}
                    color='red'
                    variant='filled'
                    onClick={onTerminateSession}
                    style={{ flex: 1 }}
                  >
                    Termina sessione
                  </Button>
                  <Button
                    leftSection={<XCircle size={18} />}
                    variant='light'
                    color='gray'
                    onClick={onCancelSession}
                    style={{ flex: 1 }}
                  >
                    Annulla sessione
                  </Button>
                </Group>
                {showForceStartExperience && onForceStartExperience && (
                  <Button
                    leftSection={<PlayCircle size={18} />}
                    color='orange'
                    variant='filled'
                    fullWidth
                    onClick={onForceStartExperience}
                  >
                    Forza avvio esperienza
                  </Button>
                )}
              </Stack>
            )}
          </>
        )}

        {sessionState !== 'in-progress' &&
          sessionState !== 'queued' &&
          sessionState !== 'initializing' &&
          !(isPending && sessionState === 'queued') && (
            <Text size='sm' c='dimmed'>
              {sessionState === 'completed' &&
                'La sessione è stata completata.'}
            </Text>
          )}
      </Stack>
    </Card>
  );
};

interface BaseSessionControlsContentProps {
  sessionState: string;
  baseline: number;
  isSensorConnected: boolean;
  isVrConnected: boolean;
  sensorStatus: string;
  deviceStatus: string;
  appRequirements: { vr: boolean; sensor: boolean };
  onInitSession: () => void;
  onTerminateSession: () => Promise<void>;
  onCancelSession: () => Promise<void>;
  onForceStartExperience?: () => void;
  showForceStartExperience?: boolean;
  onStartBaseline?: () => void;
  onSkipBaseline?: () => void;
  initSessionPreview?: {
    eventType: 'initSession' | 'startSession';
    payload: Record<string, unknown>;
  };
  t: ReturnType<typeof useTranslation>['t'];
  session?: DeviceConfigurationApi | undefined;
  isPending: boolean;
}

const BaseSessionControlsContent = ({
  sessionState,
  baseline,
  isSensorConnected,
  isVrConnected,
  sensorStatus,
  deviceStatus,
  appRequirements,
  onInitSession,
  onTerminateSession,
  onCancelSession,
  onForceStartExperience,
  showForceStartExperience,
  onStartBaseline,
  onSkipBaseline,
  initSessionPreview,
  t,
  session,
  isPending,
}: BaseSessionControlsContentProps) => {
  if (sessionState === 'loading') {
    return <SessionLoadingCard />;
  }

  return (
    <SessionStatusCard
      sessionState={sessionState}
      session={session}
      baseline={baseline}
      isSensorConnected={isSensorConnected}
      isVrConnected={isVrConnected}
      sensorStatus={sensorStatus}
      deviceStatus={deviceStatus}
      appRequirements={appRequirements}
      isPending={isPending}
      onInitSession={onInitSession}
      onTerminateSession={onTerminateSession}
      onCancelSession={onCancelSession}
      onForceStartExperience={onForceStartExperience}
      showForceStartExperience={showForceStartExperience}
      onStartBaseline={onStartBaseline}
      onSkipBaseline={onSkipBaseline}
      initSessionPreview={initSessionPreview}
      t={t}
    />
  );
};

// ============================================================================
// Main Session Controls Component
// ============================================================================

export const SessionControls = ({
  session,
  appRequirements,
  allDevices,
  connectionStatus,
}: SessionControlsProps) => {
  const base = useSessionControlsBase(session, allDevices, connectionStatus);
  const authUser = useAuthUser();
  const showForceStartExperience =
    authUser?.email === 'cnr-calabria@become-hub.com';

  // Calcola isSensorConnected per la logica dinamica di requiresBaseline
  const deviceState = useMemo(() => {
    return allDevices.map(device => {
      const clientId = device.clientId ?? device.code;
      return {
        ...device,
        status:
          connectionStatus[clientId ?? ''] ?? device.status ?? 'disconnected',
      };
    });
  }, [allDevices, connectionStatus]);

  const isSensorConnected =
    getDeviceStatus(deviceState, 'smartwatch') === 'connected';

  const needsBaseline = requiresBaseline(session?.type, isSensorConnected);
  const isDebugModeActive = useDebugEnabled();

  // Pulsante "Inizia sessione" generico che si adatta automaticamente:
  // - Per performance/exposure (o quando sensore è connesso): chiama initSession → stato initializing → mostra baseline calculator
  // - Per altri tipi: chiama direttamente startExperience → avvia la sessione
  const onInitSession = useCallback(() => {
    if (needsBaseline) {
      // Per performance/exposure: init → initializing → baseline calculator → auto start
      base.initSession();
    } else {
      // Per altri tipi: avvia direttamente la sessione
      base.startExperience();
    }
  }, [needsBaseline, base.initSession, base.startExperience]);

  const initSessionPreview = useMemo(() => {
    if (!isDebugModeActive) {
      return undefined;
    }

    if (!session) {
      return undefined;
    }

    const initPayload = {
      sessionId: String(session.id),
      config: buildLegacyInitSessionConfig(session),
    };

    const startPayload = {
      code: session.deviceCode,
      command: 'start',
      map: session.scenario,
    };

    if (needsBaseline) {
      return {
        eventType: 'initSession' as const,
        payload: initPayload,
      };
    }

    const isBioFeedback = appConfigs[session.type]?.defaultVariation > 0;
    if (isBioFeedback) {
      return {
        eventType: 'startSession' as const,
        payload: startPayload,
      };
    }

    return {
      eventType: 'initSession' as const,
      payload: initPayload,
    };
  }, [isDebugModeActive, needsBaseline, session]);

  return (
    <BaseSessionControlsContent
      sessionState={base.sessionState}
      baseline={base.baseline}
      isSensorConnected={base.isSensorConnected}
      isVrConnected={base.isVrConnected}
      sensorStatus={base.sensorStatus}
      deviceStatus={base.deviceStatus}
      appRequirements={appRequirements}
      onInitSession={onInitSession}
      onTerminateSession={base.terminateSession}
      onCancelSession={base.cancelSession}
      onForceStartExperience={base.forceStartExperience}
      showForceStartExperience={showForceStartExperience}
      onStartBaseline={base.startBaseline}
      onSkipBaseline={base.skipBaseline}
      initSessionPreview={initSessionPreview}
      t={base.t}
      session={session}
      isPending={base.isPending}
    />
  );
};

interface SessionStatsProps {
  session: DeviceConfigurationApi | undefined;
}

export const SessionStats = React.memo<SessionStatsProps>(({ session }) => {
  const variation = session?.config?.variation || 0;

  return (
    <Stack gap={'xs'} w={'100%'} flex={1}>
      {variation > 0 && <StatPhase />}
    </Stack>
  );
});

SessionStats.displayName = 'SessionStats';

// ============================================================================
// Main Component
// ============================================================================

export default function SessionId() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    session,
    isLoading: sessionLoading,
    deleteSession,
    isDeleting,
  } = useSession(id);

  console.log('Session', session);

  const user = useAuthUser();
  const userId = user?.id?.toString();

  const { devices: allDevices, isLoading: devicesLoading } =
    useUserDevices(userId);

  const { connectionStatus } = useDeviceConnectionsStatus(allDevices);

  const appRequirements = useMemo(() => {
    if (!session?.config) {
      return appConfigs.default.devices || { vr: false, sensor: false };
    }
    return (
      appConfigs[session.type as keyof typeof appConfigs]?.devices ||
      appConfigs.default.devices || { vr: false, sensor: false }
    );
  }, [id]);

  const isLoading = sessionLoading || devicesLoading;

  const storeSessionId = useHeartRateStore(s => s.sessionId);
  const clearForSession = useHeartRateStore(s => s.clearForSession);

  useEffect(() => {
    if (!id) return;
    if (storeSessionId !== id) {
      clearForSession(id);
    }
  }, [id, storeSessionId, clearForSession]);

  // Sincronizza il baseline dalla sessione allo store (per calcolo phase p0/p1/p-1 etc.)
  useEffect(() => {
    const sessionBaseline = session?.baseline;
    if (typeof sessionBaseline === 'number' && sessionBaseline > 0) {
      useBaselineCalculationStore.getState().setBaseline(sessionBaseline);
    }
  }, [session?.baseline]);

  // Redirect to completed session page if session is completed
  useEffect(() => {
    if (session && session.status === 'completed' && id) {
      navigate(`/sessions/completed/${id}`, { replace: true });
    }
  }, [session, id, navigate]);

  if (isLoading) {
    return (
      <div
        style={{
          position: 'relative',
          minHeight: 'calc(100vh - 85px)',
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          boxSizing: 'border-box',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Loader size='lg' />
        <Text mt='md' c='dimmed'>
          Loading session...
        </Text>
      </div>
    );
  }

  if (!session) {
    console.log('Session not found', session);

    return (
      <div
        style={{
          position: 'relative',
          minHeight: 'calc(100vh - 85px)',
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          boxSizing: 'border-box',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text c='red'>Session not found</Text>
      </div>
    );
  }

  const ScenarioLayout = getScenarioLayout(session.type);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: 'calc(100vh - 85px)',
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
    >
      <SessionHeader
        sessionId={session.id}
        deleteSession={deleteSession}
        isDeleting={isDeleting}
        sessionStatus={session.status}
      />
      <Divider mt='md' mb='lg' />
      <ScenarioLayout
        session={session}
        appRequirements={appRequirements}
        allDevices={allDevices}
        connectionStatus={connectionStatus}
        onConfigureClick={() => navigate('/devices')}
        isLoading={isLoading}
      />
    </div>
  );
}
